import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { GanttChart } from "@/components/Gantt/GanttChart";
import { TaskModal } from "@/components/Tasks/TaskModal";
import { ProjectModal } from "@/components/Projects/ProjectModal";
import { PhaseModal } from "@/components/Projects/PhaseModal";
import { DatePicker } from "@/components/common/DatePicker";
import { useAuth } from "@/context/AuthContext";
import { DEPARTMENTS } from "@/constants";
import { Plus, PencilSimple, DotsSixVertical, Trash, Archive, ArrowCounterClockwise, Warning, Flag, CheckSquare, EnvelopeSimple, Bell, Copy } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [gantt, setGantt] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [delayLog, setDelayLog] = useState({ entries: [], total: 0 });
  const [taskModal, setTaskModal] = useState(null);
  const [phaseModal, setPhaseModal] = useState(null);
  const [editProject, setEditProject] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notifying, setNotifying] = useState(false);

  const sendNotifications = async () => {
    setNotifying(true);
    try {
      const { data } = await api.post(`/projects/${id}/send-assignment-email`);
      if (data.total_recipients === 0) toast.info("No one is assigned to any task on this project yet");
      else if (data.sent.length === 0) toast.warning("In-app notifications sent, but emails failed — check Microsoft Graph is configured");
      else toast.success(`Notified ${data.sent.length} of ${data.total_recipients} assignee${data.total_recipients > 1 ? "s" : ""} (email + in-app)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send notifications");
    } finally {
      setNotifying(false);
    }
  };
  const [milestoneForm, setMilestoneForm] = useState({ name: "", target_date: "" });
  const [showPhaseForm, setShowPhaseForm] = useState(false);
  const [phaseForm, setPhaseForm] = useState({ name: "", department: "", start_date: "", target_end_date: "" });
  const [dragTaskId, setDragTaskId] = useState(null);
  const printRef = useRef(null);
  const canEdit = user?.role === "admin" || user?.role === "pm";
  const isProjectCreator = user?.role === "admin" || (!!project && (project.created_by || project.owner_id) === user?.id);
  const isEditor = !!project?.editors?.includes(user?.id);
  const canEditTimeline = user?.role === "admin" || (project && (project.created_by || project.owner_id) === user?.id);
  const canReorder = user?.role === "admin" || project?.owner_id === user?.id;
  const canDelete = user?.role === "admin" || (project && (project.created_by || project.owner_id) === user?.id);

  const load = async () => {
    const [{ data: p }, { data: g }, { data: t }, { data: m }, { data: a }, { data: dl }] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/projects/${id}/gantt`),
      api.get("/tasks", { params: { project_id: id } }),
      api.get(`/projects/${id}/milestones`),
      api.get(`/projects/${id}/audit-log`),
      api.get(`/projects/${id}/delay-log`),
    ]);
    setProject(p);
    setGantt(g);
    setTasks(t);
    setMilestones(m);
    setAuditLog(a);
    setDelayLog(dl);
  };

  useEffect(() => { load(); }, [id]);

  const onHold = project?.status === "On Hold";
  const ganttRows = gantt ? [
    ...gantt.phases.flatMap((ph) => [
      { id: ph.id, type: "phase", label: ph.name, department: ph.department, start: ph.start_date, end: ph.target_end_date, status: ph.status, completion_pct: ph.completion_pct, onHold },
      ...ph.tasks.map((t) => ({ id: t.id, type: "task", label: t.title, department: t.department, start: t.start_date, end: t.due_date, status: t.status, dependencies: t.dependencies, draggable: canEditTimeline, completion_pct: t.completion_pct, actual_completion_date: t.actual_completion_date, assignee_names: t.assignee_names, onHold })),
    ]),
    ...(gantt.unassigned_tasks?.length > 0 ? [
      { id: "unassigned-header", type: "phase", label: "Unassigned Tasks", start: null, end: null },
      ...gantt.unassigned_tasks.map((t) => ({ id: t.id, type: "task", label: t.title, department: t.department, start: t.start_date, end: t.due_date, status: t.status, dependencies: t.dependencies, draggable: canEditTimeline, completion_pct: t.completion_pct, actual_completion_date: t.actual_completion_date, assignee_names: t.assignee_names, onHold })),
    ] : []),
  ] : [];

  const addPhase = async (e) => {
    e.preventDefault();
    if (!phaseForm.name.trim() || !phaseForm.department) return;
    try {
      await api.post(`/projects/${id}/phases`, phaseForm);
      setPhaseForm({ name: "", department: "", start_date: "", target_end_date: "" });
      setShowPhaseForm(false);
      toast.success("Phase added");
      load();
    } catch { toast.error("Failed to add phase"); }
  };

  const handleReschedule = async (taskId, start, end) => {
    try {
      await api.patch(`/tasks/${taskId}/reschedule`, { start_date: start, due_date: end });
      toast.success("Task rescheduled");
      load();
    } catch { toast.error("Failed to reschedule"); }
  };

  const addMilestone = async (e) => {
    e.preventDefault();
    if (!milestoneForm.name.trim() || !milestoneForm.target_date) return;
    await api.post(`/projects/${id}/milestones`, milestoneForm);
    setMilestoneForm({ name: "", target_date: "" });
    load();
  };

  const updateMilestoneStatus = async (mId, status) => {
    await api.put(`/milestones/${mId}`, { status });
    load();
  };
  const [sapDialog, setSapDialog] = useState(false);
  const [sapCC, setSapCC] = useState("P2-NPD");
  const [sapRelease, setSapRelease] = useState(true);
  const [sapPushing, setSapPushing] = useState(false);
  const pushToSap = async () => {
    setSapPushing(true);
    try {
      const { data } = await api.post(`/projects/${id}/push-to-sap`, { cost_centre: sapCC, release: sapRelease });
      toast.success(`Created in SAP as ${data.sap_project_id} (${data.sap_status}) — ${data.phases_created} phases, ${data.tasks_created} tasks`);
      if (data.errors?.length) toast.warning(`${data.errors.length} item(s) failed: ${data.errors[0]}`);
      setSapDialog(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || "SAP push failed"); }
    finally { setSapPushing(false); }
  };
  const [sapReleasing, setSapReleasing] = useState(false);
  const releaseSap = async () => {
    setSapReleasing(true);
    try {
      const { data } = await api.post(`/projects/${id}/sap-release`);
      toast.success(`SAP project ${data.sap_project_id} is now ${data.sap_status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || "SAP release failed"); }
    finally { setSapReleasing(false); }
  };

  const [cloning, setCloning] = useState(false);
  const cloneProject = async () => {
    setCloning(true);
    try {
      const { data } = await api.post(`/projects/${id}/clone`);
      toast.success(`Cloned as "${data.name}" (draft) — ${data.phases} phases, ${data.tasks} tasks, ${data.milestones} milestones. Edit freely, then Save Project to publish.`);
      navigate(`/projects/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || "Clone failed"); }
    finally { setCloning(false); }
  };

  const [reminding, setReminding] = useState(null);
  const sendReminder = async (taskId) => {
    setReminding(taskId);
    try {
      const { data } = await api.post(`/tasks/${taskId}/send-reminder`);
      toast.success(`Reminder sent to ${data.reminded} assignee${data.reminded > 1 ? "s" : ""} (email + in-app)`);
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || "Failed to send reminder"); }
    finally { setReminding(null); }
  };

  const [linkingMs, setLinkingMs] = useState(null);
  const [linkSel, setLinkSel] = useState([]);
  const openLinkTasks = (m) => { setLinkingMs(m); setLinkSel(m.task_ids || []); };
  const saveLinkTasks = async () => {
    try {
      await api.put(`/milestones/${linkingMs.id}`, { task_ids: linkSel });
      toast.success(linkSel.length ? `${linkSel.length} task(s) linked — milestone will auto-achieve when they're all completed` : "Links cleared — using target-date rule");
      setLinkingMs(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || "Failed to link tasks"); }
  };

  const openTaskById = async (taskId) => {
    const { data } = await api.get(`/tasks/${taskId}`);
    setTaskModal(data);
  };

  const handleDrop = async (targetTaskId) => {
    if (!dragTaskId || dragTaskId === targetTaskId) { setDragTaskId(null); return; }
    const ids = tasks.map((t) => t.id);
    const fromIdx = ids.indexOf(dragTaskId);
    const toIdx = ids.indexOf(targetTaskId);
    ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, dragTaskId);
    const reordered = ids.map((tid) => tasks.find((t) => t.id === tid));
    setTasks(reordered);
    setDragTaskId(null);
    try {
      await api.patch("/tasks/reorder", { project_id: id, ordered_task_ids: ids });
    } catch {
      toast.error("Failed to save task order");
      load();
    }
  };

  const deleteProject = async () => {
    setDeleting(true);
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      navigate("/projects");
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || "Failed to delete project");
      setDeleting(false);
    }
  };

  const [publishing, setPublishing] = useState(false);
  const publishProject = async () => {
    setPublishing(true);
    try {
      await api.put(`/projects/${id}`, { is_draft: false });
      toast.success("Project saved & published — you can now send notifications");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || "Failed to publish project");
    } finally {
      setPublishing(false);
    }
  };

  const toggleArchive = async () => {
    try {
      await api.put(`/projects/${id}`, { archived: !project.archived });
      toast.success(project.archived ? "Project restored" : "Project archived");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || "Failed to update project");
    }
  };

  if (!project) return <div className="p-10 text-center font-mono text-muted-foreground">&gt; loading project...</div>;

  return (
    <div data-testid="project-detail-page">
      <div ref={printRef}>
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <span className="label-caps">{project.product_line}</span>
          <div className="flex items-center gap-3 mt-1">
            <h1 className="text-2xl font-display font-extrabold" data-testid="project-detail-name">{project.name}</h1>
            <StatusBadge status={project.status} />
            {project.is_draft && <span data-testid="project-draft-badge" className="text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400" style={{ borderRadius: "2px" }}>Draft</span>}
            {project.archived && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-muted text-muted-foreground" style={{ borderRadius: "2px" }} data-testid="project-archived-badge">Archived</span>}
          </div>
          <p className="text-sm text-muted-foreground mt-1">{project.description}</p>
          {onHold && (
            <div data-testid="project-hold-banner" className="mt-2 px-3 py-2 bg-amber-50 border border-amber-300 text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/40 dark:text-amber-300 text-sm" style={{ borderRadius: "2px" }}>
              <span className="font-bold uppercase text-xs font-mono mr-2">On Hold</span>
              {project.hold_reason || "No reason provided"}
              <span className="block text-xs opacity-70 mt-0.5">Overdue & delay tracking is paused until the project resumes</span>
            </div>
          )}
        </div>
        {(isProjectCreator || canDelete) && (
          <div className="flex items-center gap-2">
            {isProjectCreator && (
              <>
                {!project.is_draft && !project.sap_project_id && (
                  <button data-testid="push-to-sap-btn" onClick={() => setSapDialog(true)}
                    className="flex items-center gap-2 px-4 py-2 text-xs font-mono font-bold uppercase tracking-wide border border-foreground hover:bg-foreground hover:text-background"
                    style={{ borderRadius: "2px" }}>
                    Create in SAP
                  </button>
                )}
                {project.sap_project_id && (
                  <>
                    <span data-testid="sap-id-badge" className="text-[10px] font-mono uppercase px-2 py-1 bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300" style={{ borderRadius: "2px" }}>
                      SAP: {project.sap_project_id}{project.sap_status ? ` · ${project.sap_status}` : ""}
                    </span>
                    {project.sap_status !== "Released" && (
                      <button data-testid="sap-release-btn" onClick={releaseSap} disabled={sapReleasing}
                        className="flex items-center gap-2 px-3 py-2 text-xs font-mono font-bold uppercase tracking-wide border border-foreground hover:bg-foreground hover:text-background disabled:opacity-50"
                        style={{ borderRadius: "2px" }}>
                        {sapReleasing ? "Releasing..." : "Release in SAP"}
                      </button>
                    )}
                  </>
                )}
                {project.is_draft && (
                  <button data-testid="publish-project-btn" onClick={publishProject} disabled={publishing}
                    className="flex items-center gap-2 px-4 py-2 text-xs font-mono font-bold uppercase tracking-wide bg-foreground text-background hover:opacity-90 disabled:opacity-50"
                    style={{ borderRadius: "2px" }}>
                    <CheckSquare size={14} />{publishing ? "Saving..." : "Save Project"}
                  </button>
                )}
                <button data-testid="send-notifications-btn" onClick={sendNotifications} disabled={notifying}
                  className="btn-outline-navy flex items-center gap-2 disabled:opacity-50">
                  <EnvelopeSimple size={14} />{notifying ? "Sending..." : "Send Notifications"}
                </button>
                <button data-testid="edit-project-btn" onClick={() => setEditProject(true)} className="btn-outline-navy flex items-center gap-2"><PencilSimple size={14} />Edit Project</button>
                <button data-testid="clone-project-btn" onClick={cloneProject} disabled={cloning} className="btn-outline-navy flex items-center gap-2 disabled:opacity-50">
                  <Copy size={14} />{cloning ? "Cloning..." : "Clone"}
                </button>
                <button data-testid="archive-project-btn" onClick={toggleArchive} className="btn-outline-navy flex items-center gap-2">
                  {project.archived ? <><ArrowCounterClockwise size={14} />Restore</> : <><Archive size={14} />Archive</>}
                </button>
              </>
            )}
            {canDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="delete-project-btn" className="p-2 border border-border hover:bg-destructive/10 hover:border-destructive text-destructive" style={{ borderRadius: "2px" }}>
                    <Trash size={16} />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="delete-project-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete "{project.name}"?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently deletes the project along with all its phases, tasks, milestones, comments and audit history. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="delete-project-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid="delete-project-confirm-btn" onClick={deleteProject} disabled={deleting}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      {deleting ? "Deleting..." : "Delete Project"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </div>

      <div className="card-flat p-4 mb-4 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="project-overview-panel">
        <div>
          <div className="label-caps">Owner</div>
          <div className="text-sm font-medium mt-0.5" data-testid="project-overview-owner">{project.owner_name || "Unassigned"}</div>
        </div>
        <div>
          <div className="label-caps">Start Date</div>
          <div className="text-sm font-medium mt-0.5" data-testid="project-overview-start-date">{project.start_date ? format(new Date(project.start_date), "d MMM yyyy") : "—"}</div>
        </div>
        <div>
          <div className="label-caps">Target Production</div>
          <div className="text-sm font-medium mt-0.5" data-testid="project-overview-target-date">{project.target_production_date ? format(new Date(project.target_production_date), "d MMM yyyy") : "—"}</div>
        </div>
        <div>
          <div className="label-caps">Budget</div>
          <div className="text-sm font-medium mt-0.5" data-testid="project-overview-budget">{project.budget != null ? `$${Number(project.budget).toLocaleString()}` : "—"}</div>
        </div>
        <div>
          <div className="label-caps">Tasks</div>
          <div className="text-sm font-medium mt-0.5" data-testid="project-overview-task-count">{project.task_count ?? 0} total{project.overdue_count > 0 ? ` · ${project.overdue_count} overdue` : ""}</div>
        </div>
        <div className="col-span-2 sm:col-span-2">
          <div className="flex items-center justify-between mb-1">
            <div className="label-caps">Completion</div>
            <span className="text-xs font-mono font-semibold text-primary" data-testid="project-overview-completion-pct">{project.completion_pct ?? 0}%</span>
          </div>
          <div className="w-full h-1.5 bg-muted overflow-hidden" style={{ borderRadius: "2px" }}>
            <div className="h-full bg-primary" style={{ width: `${project.completion_pct ?? 0}%` }} />
          </div>
        </div>
      </div>

      <Tabs defaultValue="gantt">
        <TabsList>
          <TabsTrigger data-testid="tab-gantt" value="gantt">Gantt</TabsTrigger>
          <TabsTrigger data-testid="tab-tasks" value="tasks">Tasks ({tasks.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-milestones" value="milestones">Milestones</TabsTrigger>
          <TabsTrigger data-testid="tab-delay-log" value="delays" className="flex items-center gap-1.5">
            Delay Log
            {delayLog.total > 0 && (
              <span data-testid="delay-log-count-badge" className="text-[10px] font-mono font-bold px-1.5 py-0.5 bg-destructive text-destructive-foreground leading-none" style={{ borderRadius: "2px" }}>
                {delayLog.total}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger data-testid="tab-audit" value="audit">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="gantt">
          {(isProjectCreator || isEditor) && (
            <div className="flex justify-end mb-3">
              <button data-testid="add-phase-btn" onClick={() => setShowPhaseForm((v) => !v)} className="btn-outline-navy flex items-center gap-2"><Plus size={14} />Add Phase</button>
            </div>
          )}
          {showPhaseForm && (
            <form onSubmit={addPhase} className="card-flat p-4 mb-3 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
              <input data-testid="phase-name-input" required placeholder="Phase name" value={phaseForm.name} onChange={(e) => setPhaseForm({ ...phaseForm, name: e.target.value })}
                className="border border-border px-3 py-2 text-sm bg-background h-9" style={{ borderRadius: "2px" }} />
              <Select value={phaseForm.department} onValueChange={(v) => setPhaseForm({ ...phaseForm, department: v })}>
                <SelectTrigger data-testid="phase-department-select" style={{ borderRadius: "2px" }}><SelectValue placeholder="Department" /></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              <DatePicker testId="phase-start-date-input" value={phaseForm.start_date} onChange={(v) => setPhaseForm({ ...phaseForm, start_date: v })} placeholder="Start date" />
              <DatePicker testId="phase-end-date-input" value={phaseForm.target_end_date} onChange={(v) => setPhaseForm({ ...phaseForm, target_end_date: v })} placeholder="Target end date" />
              <button data-testid="save-phase-btn" className="btn-navy">Save Phase</button>
            </form>
          )}
          <GanttChart rows={ganttRows} milestones={gantt?.milestones || []} onReschedule={handleReschedule} canDrag={canEdit} onTaskClick={openTaskById}
            onPhaseClick={(isProjectCreator || isEditor) ? (pid) => { const ph = gantt?.phases.find((p) => p.id === pid); if (ph) setPhaseModal(ph); } : undefined}
            exportRef={printRef} exportFileName={project.name} />
        </TabsContent>

        <TabsContent value="tasks">
          <div className="flex justify-end mb-3">
            {(canEdit || isEditor) && <button data-testid="add-task-btn" onClick={() => setTaskModal({})} className="btn-navy flex items-center gap-2"><Plus size={14} />Add Task</button>}
          </div>
          <div className="card-flat divide-y divide-border">
            {tasks.map((t) => (
              <div key={t.id} data-testid={`task-row-${t.id}`} onClick={() => setTaskModal(t)}
                draggable={canReorder}
                onDragStart={(e) => { e.stopPropagation(); setDragTaskId(t.id); }}
                onDragOver={(e) => canReorder && e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(t.id); }}
                className={`p-3 flex items-center justify-between hover:bg-muted cursor-pointer ${dragTaskId === t.id ? "opacity-40" : ""}`}>
                <div className="flex items-center gap-2">
                  {canReorder && <DotsSixVertical size={16} weight="bold" className="text-muted-foreground shrink-0 cursor-grab" data-testid={`task-drag-handle-${t.id}`} />}
                  <div>
                    <div className="text-sm font-medium">{t.title}</div>
                    <div className="text-xs text-muted-foreground">{t.department} · {t.assignee_name || "Unassigned"} · {t.due_date ? format(new Date(t.due_date), "d MMM") : "—"} · {t.completion_pct || 0}%</div>
                  </div>
                </div>
                <StatusBadge status={t.status} />
              </div>
            ))}
            {tasks.length === 0 && <div className="p-4 text-sm font-mono text-muted-foreground">&gt; no tasks yet</div>}
          </div>
        </TabsContent>

        <TabsContent value="milestones">
          {canEdit && (
            <form onSubmit={addMilestone} className="flex gap-2 mb-4">
              <input data-testid="milestone-name-input" required placeholder="Milestone name" value={milestoneForm.name} onChange={(e) => setMilestoneForm({ ...milestoneForm, name: e.target.value })}
                className="flex-1 border border-border px-3 py-2 text-sm bg-background" style={{ borderRadius: "2px" }} />
              <div className="w-48"><DatePicker testId="milestone-date-input" value={milestoneForm.target_date} onChange={(v) => setMilestoneForm({ ...milestoneForm, target_date: v })} placeholder="Target date" /></div>
              <button data-testid="add-milestone-btn" className="btn-navy">Add</button>
            </form>
          )}
          <div className="space-y-2">
            {milestones.map((m) => (
              <div key={m.id} data-testid={`milestone-row-${m.id}`} className="card-flat p-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{m.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {m.target_date ? format(new Date(m.target_date), "d MMM yyyy") : "—"}
                    {(m.task_ids || []).length > 0 && (
                      <span className="ml-2 text-primary">
                        · {tasks.filter((t) => m.task_ids.includes(t.id) && t.status === "Completed").length}/{m.task_ids.length} linked tasks done
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canEdit && (
                    <button data-testid={`milestone-link-tasks-btn-${m.id}`} onClick={() => openLinkTasks(m)}
                      className="text-xs px-2 py-1 border border-border hover:bg-muted" style={{ borderRadius: "2px" }}>
                      Link tasks{(m.task_ids || []).length > 0 ? ` (${m.task_ids.length})` : ""}
                    </button>
                  )}
                  {canEdit ? (
                    <select data-testid={`milestone-status-select-${m.id}`} value={m.status} onChange={(e) => updateMilestoneStatus(m.id, e.target.value)}
                      className="border border-border text-xs px-2 py-1 bg-background" style={{ borderRadius: "2px" }}>
                      <option value="Pending">Pending</option><option value="Achieved">Achieved</option><option value="Missed">Missed</option>
                    </select>
                  ) : <StatusBadge status={m.status} />}
                </div>
              </div>
            ))}
          </div>
          {linkingMs && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={() => setLinkingMs(null)}>              <div className="bg-background border border-border p-5 w-full max-w-md max-h-[70vh] flex flex-col" style={{ borderRadius: "2px" }}
                onClick={(e) => e.stopPropagation()} data-testid="link-tasks-dialog">
                <div className="font-semibold text-sm mb-1">Link tasks to "{linkingMs.name}"</div>
                <p className="text-xs text-muted-foreground mb-3">The milestone auto-achieves when all linked tasks are 100% — dates are ignored. Leave empty to use the target-date rule.</p>
                <div className="flex-1 overflow-y-auto space-y-1 border border-border p-2" style={{ borderRadius: "2px" }}>
                  {tasks.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                      <input type="checkbox" data-testid={`link-task-check-${t.id}`} checked={linkSel.includes(t.id)}
                        onChange={(e) => setLinkSel(e.target.checked ? [...linkSel, t.id] : linkSel.filter((x) => x !== t.id))} />
                      <span className={t.status === "Completed" ? "line-through text-muted-foreground" : ""}>{t.title}</span>
                      <span className="text-[10px] font-mono text-muted-foreground ml-auto">{t.status}</span>
                    </label>
                  ))}
                  {tasks.length === 0 && <p className="text-xs text-muted-foreground">No tasks in this project yet</p>}
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button className="btn-outline-navy" onClick={() => setLinkingMs(null)}>Cancel</button>
                  <button data-testid="save-link-tasks-btn" className="btn-navy" onClick={saveLinkTasks}>Save Links</button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="delays">
          <div data-testid="delay-log-panel">
            {delayLog.entries.length > 0 && (
              <p className="text-xs font-mono text-muted-foreground mb-3" data-testid="delay-log-summary">
                &gt; {delayLog.total} item{delayLog.total !== 1 ? "s" : ""} missed the planned timeline
              </p>
            )}
            <div className="card-flat divide-y divide-border">
              {delayLog.entries.map((e) => (
                <div key={`${e.entry_type}-${e.id}`} data-testid={`delay-log-item-${e.id}`}
                  onClick={() => e.entry_type === "task" && openTaskById(e.id)}
                  className={`p-3 flex items-center justify-between gap-3 ${e.entry_type === "task" ? "hover:bg-muted cursor-pointer" : ""}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {e.entry_type === "milestone"
                      ? <Flag size={18} weight="fill" className="text-destructive shrink-0" />
                      : e.state === "completed_late"
                        ? <CheckSquare size={18} weight="fill" className="text-amber-500 shrink-0" />
                        : <Warning size={18} weight="fill" className="text-destructive shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{e.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {e.entry_type === "milestone" ? "Milestone" : "Task"}
                        {e.assignees?.length > 0 && ` · ${e.assignees.join(", ")}`}
                        {e.due_date && ` · Due ${format(new Date(e.due_date), "d MMM yyyy")}`}
                        {e.resolved_date && ` · Completed ${format(new Date(e.resolved_date), "d MMM yyyy")}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {e.entry_type === "task" && e.state === "overdue" && canEdit && (
                      <button data-testid={`send-reminder-btn-${e.id}`}
                        onClick={(ev) => { ev.stopPropagation(); sendReminder(e.id); }}
                        disabled={reminding === e.id}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1 border border-primary text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-50"
                        style={{ borderRadius: "2px" }}>
                        <Bell size={12} weight="fill" />{reminding === e.id ? "Sending..." : "Send Reminder"}
                      </button>
                    )}
                    <span data-testid={`delay-log-days-late-${e.id}`}
                      className={`text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 ${e.state === "completed_late" ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400" : "bg-destructive/10 text-destructive"}`}
                      style={{ borderRadius: "2px" }}>
                      {e.state === "completed_late" ? `${e.days_late}d late` : e.state === "missed" ? "Missed" : `${e.days_late}d overdue`}
                    </span>
                    <StatusBadge status={e.status} />
                  </div>
                </div>
              ))}
              {delayLog.entries.length === 0 && (
                <div className="p-4 text-sm font-mono text-muted-foreground" data-testid="delay-log-empty">&gt; no delays — everything is on schedule</div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <div className="card-flat divide-y divide-border">
            {auditLog.map((a) => (
              <div key={a.id} data-testid={`audit-log-item-${a.id}`} className="p-3 text-sm">
                <span className="font-semibold">{a.user_name}</span> {a.action} — <span className="text-muted-foreground">{a.details}</span>
                <div className="text-xs text-muted-foreground mt-0.5 font-mono">{format(new Date(a.created_at), "d MMM yyyy, HH:mm")}</div>
              </div>
            ))}
            {auditLog.length === 0 && <div className="p-4 text-sm font-mono text-muted-foreground">&gt; no changes logged yet</div>}
          </div>
        </TabsContent>
      </Tabs>
      </div>

      {taskModal && <TaskModal task={taskModal.id ? taskModal : null} projectId={id} onClose={() => setTaskModal(null)} onSaved={() => { setTaskModal(null); load(); }} />}
      {editProject && <ProjectModal project={project} onClose={() => setEditProject(false)} onSaved={() => { setEditProject(false); load(); }} />}
      {phaseModal && <PhaseModal phase={phaseModal} onClose={() => setPhaseModal(null)} onSaved={load} onDeleted={load} canEditDates={canEditTimeline} />}
      {sapDialog && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={() => setSapDialog(false)}>
          <div className="bg-background border border-border p-5 w-full max-w-md" style={{ borderRadius: "2px" }} onClick={(e) => e.stopPropagation()} data-testid="sap-push-dialog">
            <div className="font-semibold text-sm mb-1">Create "{project?.name}" in SAP</div>
            <p className="text-xs text-muted-foreground mb-3">
              Creates the project in SAP ByDesign with all its phases and tasks.{" "}
              <Link to="/help/sap-push" data-testid="sap-dialog-help-link" className="underline hover:text-foreground">Work instruction</Link>
            </p>
            <label className="text-xs font-semibold">SAP Cost Centre (NPD)</label>
            <div className="flex gap-2 mt-1 mb-2">
              {["P1-NPD", "P2-NPD"].map((cc) => (
                <button key={cc} type="button" onClick={() => setSapCC(cc)}
                  className={`text-xs px-2 py-1 border ${sapCC === cc ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"}`}
                  style={{ borderRadius: "2px" }}>{cc}</button>
              ))}
              <input data-testid="sap-cost-centre-input" value={sapCC} onChange={(e) => setSapCC(e.target.value)}
                className="flex-1 border border-border px-2 py-1 text-sm bg-background" style={{ borderRadius: "2px" }} />
            </div>
            <label className="flex items-center gap-2 text-xs mt-2 cursor-pointer select-none">
              <input data-testid="sap-release-checkbox" type="checkbox" checked={sapRelease} onChange={(e) => setSapRelease(e.target.checked)} />
              Release immediately (Start &amp; Release) so material can be booked right away
            </label>
            <div className="flex justify-end gap-2 mt-4">
              <button className="btn-outline-navy" onClick={() => setSapDialog(false)}>Cancel</button>
              <button data-testid="confirm-sap-push-btn" className="btn-navy" disabled={sapPushing} onClick={pushToSap}>
                {sapPushing ? "Creating in SAP..." : "Create in SAP"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
